/**
 * Chart Utilities
 * Helper functions for chart components
 */

import { ChartConfig } from "@/components/ui/chart";
import {
  BaseChartData,
  ChartDataFormat,
  ChartDataTransform,
  ChartTheme,
} from "./types";

/**
 * Get color from config or fallback
 */
export const getChartColor = (
  config: ChartConfig,
  key: string,
  index: number,
  fallbackColors?: string[],
): string => {
  const configColor = config[key]?.color || config[key]?.theme?.light;

  if (configColor) return configColor;

  if (fallbackColors && fallbackColors[index]) {
    return fallbackColors[index];
  }

  // Default color palette
  const defaultColors = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
  ];

  return defaultColors[index % defaultColors.length];
};

/**
 * Format number for display
 */
export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
): string => {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    ...options,
  }).format(value);
};

/**
 * Format percentage
 */
export const formatPercentage = (value: number, total: number = 1): string => {
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(1)}%`;
};

/**
 * Calculate total from data
 */
export const calculateTotal = (data: BaseChartData[], key: string): number => {
  return data.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
};

/**
 * Get min and max values from data
 */
export const getDataRange = (
  data: BaseChartData[],
  keys: string[],
): { min: number; max: number } => {
  let min = Infinity;
  let max = -Infinity;

  data.forEach((item) => {
    keys.forEach((key) => {
      const value = Number(item[key]);
      if (!isNaN(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    });
  });

  return { min: min === Infinity ? 0 : min, max: max === -Infinity ? 0 : max };
};

/**
 * Generate chart config from data keys
 */
export const generateChartConfig = (
  keys: string[],
  labels?: Record<string, string>,
  colors?: string[],
): ChartConfig => {
  const config: ChartConfig = {};

  keys.forEach((key, index) => {
    config[key] = {
      label: labels?.[key] || key,
      color: colors?.[index] || `hsl(var(--chart-${(index % 5) + 1}))`,
    };
  });

  return config;
};

/**
 * Validate chart data
 */
export const validateChartData = (
  data: unknown[],
  requiredKeys: string[],
): boolean => {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn("Chart data must be a non-empty array");
    return false;
  }

  const hasRequiredKeys = requiredKeys.every((key) =>
    data.some((item) => item && typeof item === "object" && key in item),
  );

  if (!hasRequiredKeys) {
    console.warn(
      `Chart data missing required keys: ${requiredKeys.join(", ")}`,
    );
    return false;
  }

  return true;
};

/**
 * Default chart theme
 */
export const defaultChartTheme: ChartTheme = {
  primary: "hsl(var(--chart-1))",
  secondary: "hsl(var(--chart-2))",
  accent: "hsl(var(--chart-3))",
  muted: "hsl(var(--chart-4))",
  success: "hsl(142, 71%, 45%)",
  warning: "hsl(38, 92%, 50%)",
  error: "hsl(0, 84%, 60%)",
  info: "hsl(199, 89%, 48%)",
};

/**
 * Truncate label text
 */
export const truncateLabel = (text: string, maxLength: number = 20): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format axis tick
 */
export const formatAxisTick = (
  value: number | string,
  compact: boolean = false,
): string => {
  if (typeof value === "string") return truncateLabel(value);

  if (compact) {
    return formatNumber(value);
  }

  return String(value);
};

/**
 * Debounce function for resize handlers
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Format currency values
 */
export const formatCurrency = (
  value: number,
  currency: string = "USD",
  locale: string = "en-US",
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation: "compact",
    compactDisplay: "short",
  }).format(value);
};

/**
 * Calculate percentage change
 */
export const calculatePercentageChange = (
  current: number,
  previous: number,
): { value: number; formatted: string; isPositive: boolean } => {
  if (previous === 0) {
    return {
      value: current > 0 ? 100 : 0,
      formatted: current > 0 ? "+100%" : "0%",
      isPositive: current >= 0,
    };
  }

  const change = ((current - previous) / Math.abs(previous)) * 100;
  const isPositive = change >= 0;
  const formatted = `${isPositive ? "+" : ""}${change.toFixed(1)}%`;

  return {
    value: Math.abs(change),
    formatted,
    isPositive,
  };
};

/**
 * Generate gradient colors
 */
export const generateGradient = (
  startColor: string,
  endColor: string,
  steps: number = 5,
): string[] => {
  // This is a simplified gradient generator
  // In a real implementation, you might want to use a color manipulation library
  const colors = [];
  for (let i = 0; i < steps; i++) {
    const ratio = i / (steps - 1);
    // Simple interpolation between two hsl colors
    colors.push(`hsl(var(--chart-${i + 1}))`);
  }
  return colors;
};

/**
 * Sort chart data
 */
export const sortChartData = (
  data: BaseChartData[],
  key: string,
  direction: "asc" | "desc" = "asc",
): BaseChartData[] => {
  return [...data].sort((a, b) => {
    const aValue = typeof a[key] === "number" ? a[key] : String(a[key]);
    const bValue = typeof b[key] === "number" ? b[key] : String(b[key]);

    if (typeof aValue === "number" && typeof bValue === "number") {
      return direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();

    if (direction === "asc") {
      return aStr.localeCompare(bStr);
    } else {
      return bStr.localeCompare(aStr);
    }
  });
};

/**
 * Filter chart data by date range
 */
export const filterDataByDateRange = (
  data: BaseChartData[],
  dateKey: string,
  startDate: Date,
  endDate: Date,
): BaseChartData[] => {
  return data.filter((item) => {
    const itemDate = new Date(String(item[dateKey]));
    return itemDate >= startDate && itemDate <= endDate;
  });
};

/**
 * Aggregate data by time period
 */
export const aggregateDataByPeriod = (
  data: BaseChartData[],
  dateKey: string,
  valueKey: string,
  period: "day" | "week" | "month" | "year",
): BaseChartData[] => {
  const grouped = new Map<string, number>();

  data.forEach((item) => {
    const date = new Date(String(item[dateKey]));
    const value = Number(item[valueKey]) || 0;

    let groupKey: string;

    switch (period) {
      case "day":
        groupKey = date.toISOString().split("T")[0];
        break;
      case "week":
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        groupKey = weekStart.toISOString().split("T")[0];
        break;
      case "month":
        groupKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1,
        ).padStart(2, "0")}`;
        break;
      case "year":
        groupKey = String(date.getFullYear());
        break;
      default:
        groupKey = date.toISOString().split("T")[0];
    }

    grouped.set(groupKey, (grouped.get(groupKey) || 0) + value);
  });

  return Array.from(grouped.entries()).map(([date, value]) => ({
    [dateKey]: date,
    [valueKey]: value,
  }));
};

/**
 * Calculate moving average
 */
export const calculateMovingAverage = (
  data: BaseChartData[],
  valueKey: string,
  windowSize: number,
): BaseChartData[] => {
  const result: BaseChartData[] = [];

  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    const window = data.slice(start, i + 1);
    const average =
      window.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0) /
      window.length;

    result.push({
      ...data[i],
      [`${valueKey}_ma`]: Math.round(average * 100) / 100,
    });
  }

  return result;
};

/**
 * Detect chart data anomalies
 */
export const detectAnomalies = (
  data: BaseChartData[],
  valueKey: string,
  threshold: number = 2,
): (BaseChartData & { [key: string]: string | number })[] => {
  const values = data.map((item) => Number(item[valueKey]) || 0);
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance =
    values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
    values.length;
  const stdDev = Math.sqrt(variance);

  return data.map((item, index) => {
    const value = Number(item[valueKey]) || 0;
    const isAnomaly = Math.abs(value - mean) > threshold * stdDev;

    return {
      ...item,
      [`${valueKey}_anomaly`]: isAnomaly ? 1 : 0,
      [`${valueKey}_zscore`]: Math.abs((value - mean) / stdDev),
    };
  });
};

/**
 * Transform chart data based on configuration
 */
export const transformChartData = (
  data: BaseChartData[],
  transform: ChartDataTransform,
): BaseChartData[] => {
  let result = [...data];

  // Apply filters
  if (transform.filter && transform.filter.length > 0) {
    result = result.filter((item) => {
      return transform.filter!.every((filter) => {
        const value = item[filter.key];
        switch (filter.operator) {
          case "eq":
            return value === filter.value;
          case "ne":
            return value !== filter.value;
          case "gt":
            return Number(value) > Number(filter.value);
          case "gte":
            return Number(value) >= Number(filter.value);
          case "lt":
            return Number(value) < Number(filter.value);
          case "lte":
            return Number(value) <= Number(filter.value);
          case "contains":
            return String(value)
              .toLowerCase()
              .includes(String(filter.value).toLowerCase());
          default:
            return true;
        }
      });
    });
  }

  // Apply sorting
  if (transform.sort) {
    result = sortChartData(
      result,
      transform.sort.key,
      transform.sort.direction,
    );
  }

  // Apply moving averages
  if (transform.movingAverage && transform.movingAverage.length > 0) {
    transform.movingAverage.forEach((ma) => {
      result = calculateMovingAverage(result, ma.key, ma.window);
    });
  }

  // Apply aggregation
  if (transform.aggregate) {
    const grouped = new Map<string, BaseChartData[]>();

    result.forEach((item) => {
      const groupKey = String(item[transform.aggregate!.groupBy]);
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, []);
      }
      grouped.get(groupKey)!.push(item);
    });

    result = Array.from(grouped.entries()).map(([groupKey, items]) => {
      const aggregated: BaseChartData = {
        [transform.aggregate!.groupBy]: groupKey,
      };

      transform.aggregate!.functions.forEach((func) => {
        const values = items.map((item) => Number(item[func.key]) || 0);

        switch (func.type) {
          case "sum":
            aggregated[func.key] = values.reduce((sum, val) => sum + val, 0);
            break;
          case "avg":
            aggregated[func.key] =
              values.reduce((sum, val) => sum + val, 0) / values.length;
            break;
          case "min":
            aggregated[func.key] = Math.min(...values);
            break;
          case "max":
            aggregated[func.key] = Math.max(...values);
            break;
          case "count":
            aggregated[func.key] = values.length;
            break;
        }
      });

      return aggregated;
    });
  }

  return result;
};

/**
 * Format data according to chart data format configuration
 */
export const formatChartData = (
  data: BaseChartData[],
  format: ChartDataFormat,
  keys: string[],
): BaseChartData[] => {
  return data.map((item) => {
    const formattedItem = { ...item };

    keys.forEach((key) => {
      const value = item[key];
      if (typeof value === "number") {
        if (format.formatter) {
          formattedItem[`${key}_formatted`] = format.formatter(value);
        } else if (format.currency) {
          formattedItem[`${key}_formatted`] = formatCurrency(
            value,
            format.currency,
            format.locale,
          );
        } else {
          const options: Intl.NumberFormatOptions = {
            minimumFractionDigits: format.decimals || 0,
            maximumFractionDigits: format.decimals || 2,
          };

          if (format.compact) {
            options.notation = "compact";
            options.compactDisplay = "short";
          }

          formattedItem[`${key}_formatted`] = new Intl.NumberFormat(
            format.locale || "en-US",
            options,
          ).format(value);
        }
      }
    });

    return formattedItem;
  });
};

/**
 * Generate sample chart data for testing
 */
export const generateSampleData = (
  type: "line" | "bar" | "area" | "pie",
  count: number = 12,
): BaseChartData[] => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  switch (type) {
    case "pie":
      return [
        { category: "Desktop", value: 45, fill: "hsl(var(--chart-1))" },
        { category: "Mobile", value: 35, fill: "hsl(var(--chart-2))" },
        { category: "Tablet", value: 20, fill: "hsl(var(--chart-3))" },
      ];

    default:
      return Array.from({ length: count }, (_, i) => ({
        month: months[i % 12],
        sales: Math.floor(Math.random() * 5000) + 1000,
        revenue: Math.floor(Math.random() * 8000) + 2000,
        profit: Math.floor(Math.random() * 3000) + 500,
        users: Math.floor(Math.random() * 10000) + 5000,
      }));
  }
};

/**
 * Create chart configuration with theme colors
 */
export const createThemedChartConfig = (
  keys: string[],
  labels?: Record<string, string>,
  theme: ChartTheme = defaultChartTheme,
): ChartConfig => {
  const config: ChartConfig = {};
  const themeColors = Object.values(theme);

  keys.forEach((key, index) => {
    config[key] = {
      label: labels?.[key] || key.charAt(0).toUpperCase() + key.slice(1),
      color: themeColors[index % themeColors.length],
    };
  });

  return config;
};

/**
 * Merge multiple chart configurations
 */
export const mergeChartConfigs = (...configs: ChartConfig[]): ChartConfig => {
  return configs.reduce((merged, config) => {
    return { ...merged, ...config };
  }, {});
};

/**
 * Validate and sanitize chart props
 */
export const sanitizeChartProps = <T extends Record<string, any>>(
  props: T,
  defaults: Partial<T>,
): T => {
  const sanitized = { ...defaults, ...props } as any;

  // Ensure numeric values are within reasonable ranges
  if ("height" in sanitized && typeof sanitized.height === "number") {
    sanitized.height = Math.max(100, Math.min(2000, sanitized.height));
  }

  if ("strokeWidth" in sanitized && typeof sanitized.strokeWidth === "number") {
    sanitized.strokeWidth = Math.max(0.5, Math.min(10, sanitized.strokeWidth));
  }

  if ("fillOpacity" in sanitized && typeof sanitized.fillOpacity === "number") {
    sanitized.fillOpacity = Math.max(0, Math.min(1, sanitized.fillOpacity));
  }

  return sanitized as T;
};
